import { type FC, memo, type ReactNode } from "react";

import { type PriceData } from "../../../models/PriceData";
import * as PriceUtils from "../../../utils/PriceUtils";
import * as WeightUtils from "../../../utils/WeightUtils";
import { type WeightData } from "../../../models/WeightData";
import Spacer from "../../../common/components/Spacer";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../common/components/GridGappedContainer/GridGappedContainer";
import classNames from "classnames";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { useTranslation } from "react-i18next";

interface OrderTotalsProps {
  children?: ReactNode;
  totalValue: PriceData | null;
  surcharge?: PriceData;
  weight?: WeightData;
  className?: string;
  hideTaxesMsg?: boolean;
  additionalMsg?: ReactNode;
  taxInNewLine?: boolean;
  customTaxMsg?: ReactNode;
  rows?: {
    label: ReactNode;
    value: ReactNode;
  }[];
  showPriceOnRequest: boolean;
  tariffSurcharge?: boolean;
}

interface OrderTotalRowProps {
  value: PriceData | null;
  hideTaxMsg?: boolean;
  taxInNewLine?: boolean;
  additionalMsg?: ReactNode;
  customTaxMsg?: ReactNode;
  showPriceOnRequest: boolean;
  hasSurcharge?: boolean;
  tariffSurcharge?: boolean;
}

const BigText: FC<JSX.IntrinsicElements["span"]> = ({ className, ...rest }) => (
  <span className={classNames("body-large f-bold", className)} {...rest} />
);

const Row: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <div className={classNames("d-flex flex-justify-between", className)} {...rest} />
);

export const OrderTotalRow: FC<OrderTotalRowProps> = ({
  value,
  hideTaxMsg,
  taxInNewLine,
  additionalMsg,
  customTaxMsg,
  showPriceOnRequest,
  hasSurcharge,
  tariffSurcharge,
}) => {
  const { t } = useTranslation();

  const message =
    showPriceOnRequest && !userHasPermission(Permission.ADD_TO_QUOTE) && !window.SCHA.config.disableAllWS
      ? t("product.priceOnRequest")
      : "-";

  return (
    <Row className={"mb-2 mt-0 flex-direction-column"}>
      <div className={"d-flex flex-justify-between mb-1"}>
        <div className={"price_tax_wrapper"}>
          <BigText className={"mr-1"}>{t("cartPage.totalPrice")}</BigText>
          {additionalMsg && <div>{additionalMsg}</div>}
          {!hideTaxMsg && (
            <span className={classNames(!taxInNewLine && "mr-1")}>
              {customTaxMsg ? customTaxMsg : t("cartPage.taxes")}
            </span>
          )}
        </div>
        <BigText data-test-id={"total-price"}>
          {value ? PriceUtils.text2(value, message) : "..."}
          {!!(value && (hasSurcharge || tariffSurcharge)) && "*"}
        </BigText>
      </div>
      <div>
        <div className="d-flex flex-align-start w-100 flex-direction-column"></div>
      </div>
    </Row>
  );
};

const TotalsRow = (props: { label: ReactNode; value: ReactNode }) => (
  <div className="scha__order-totals__row">
    <div className="mr-2 scha__order-totals__row__label">{props.label}</div>
    <div className="scha__order-totals__row__value">{props.value}</div>
  </div>
);

const OrderTotals: FC<OrderTotalsProps> = (props) => {
  const {
    totalValue,
    children,
    className,
    weight,
    hideTaxesMsg,
    taxInNewLine,
    rows,
    additionalMsg,
    customTaxMsg,
    showPriceOnRequest,
    surcharge,
    tariffSurcharge,
  } = props;
  const { t } = useTranslation();

  const hasSurcharge = surcharge !== undefined;

  return (
    <div className={className}>
      {weight !== undefined && (
        <Row className={"mb-2 mt-0"}>
          <span>{t("cartPage.totalWeight")}</span>
          <span data-test-id={"total-weight"}>{WeightUtils.text(weight)}</span>
        </Row>
      )}
      {hasSurcharge && (
        <Row className={"mb-2 mt-0"}>
          <span>{t("page.cart.totals.surcharge")}</span>
          <span data-test-id={"total-weight"}>{PriceUtils.text(surcharge)}</span>
        </Row>
      )}
      <OrderTotalRow
        hideTaxMsg={hideTaxesMsg}
        taxInNewLine={taxInNewLine}
        value={totalValue}
        additionalMsg={additionalMsg}
        customTaxMsg={customTaxMsg}
        showPriceOnRequest={showPriceOnRequest}
        hasSurcharge={hasSurcharge}
        tariffSurcharge={tariffSurcharge}
      />
      {rows && (
        <>
          {rows.map((row) => (
            <TotalsRow value={row.value} label={row.label} />
          ))}
        </>
      )}
      {children && (
        <>
          <Spacer className={"mt-3 mb-30"} />
          <GridGappedContainer className={"d-flex flex-justify-end"} gapSize={GridGappedContainerSize.S_32}>
            {children}
          </GridGappedContainer>
        </>
      )}
    </div>
  );
};

export default memo(OrderTotals);
